import { type ComponentPropsWithoutRef, createRef, useState } from 'react';
import styled from 'styled-components';
import type { Rating } from './shared/types';
import Icon from 'components/Icon';

const Wrapper = styled.div`
    display: flex;
    flex: none;
    max-width: 245px;
    height: 40px;
    touch-action: none;
`;

const Star = styled(Icon)<{ $active: boolean }>`
    width: 20%;
    height: 100%;
    pointer-events: all;
    cursor: pointer;
    fill: ${({ theme, $active }) => ($active ? theme.ComponentsRatingActive : theme.ComponentsRatingInactive)};
`;

type InteractiveRatingStarsProps = {
    onRatingSelect: (rating: Rating) => void;
    onRatingHover?: (rating: Rating) => void;
    hoverRating?: number;
    rating?: Rating;
} & ComponentPropsWithoutRef<'div'>;

type InteractiveRatingStarsUncontrolledProps = Omit<InteractiveRatingStarsProps, 'value'>;

export function InteractiveRatingStarsControlled({
    onRatingSelect,
    onRatingHover,
    hoverRating = 0,
    rating: value = 0,
    ...rest
}: InteractiveRatingStarsProps) {
    const refs = Array.from(Array(5)).map(() => createRef<HTMLElement>());

    function setRating(rating: Rating) {
        onRatingSelect(rating);
    }

    function onTouchMove(event: React.TouchEvent) {
        const elementFromPoint = document.elementFromPoint(event.touches[0].clientX, event.touches[0].clientY);
        const index = refs.findIndex((ref) => ref.current?.isSameNode(elementFromPoint));
        const rating = index !== -1 && index + 1;

        if (rating && rating !== value) {
            setRating(rating as Rating);
        }
    }

    return (
        <Wrapper {...rest} onTouchMove={onTouchMove}>
            {refs.map((ref, i) => {
                const starIndex = i + 1;
                const active = hoverRating ? hoverRating >= starIndex : value >= starIndex;

                return (
                    <Star
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        ref={ref}
                        $active={active}
                        type="StarFilled"
                        size={24}
                        tabIndex={0}
                        aria-label={`${starIndex} star`}
                        onKeyDown={(e) => {
                            if (e.keyCode === 32) {
                                onRatingSelect(starIndex as Rating);
                            }
                        }}
                        onMouseEnter={() => onRatingHover?.(starIndex as Rating)}
                        onPointerDown={() => onRatingSelect(starIndex as Rating)}
                    />
                );
            })}
        </Wrapper>
    );
}

function InteractiveRatingStars({
    onRatingSelect,
    onRatingHover,
    ...rest
}: InteractiveRatingStarsUncontrolledProps): JSX.Element {
    const [ratingState, setRatingState] = useState<Rating>();

    function handleRatingSelect(index: Rating) {
        setRatingState(index);
        onRatingSelect(index);
    }

    return (
        <InteractiveRatingStarsControlled
            onRatingSelect={handleRatingSelect}
            onRatingHover={onRatingHover}
            rating={ratingState}
            {...rest}
        />
    );
}

export default InteractiveRatingStars;
